import type { CreateTypePropertyInput } from '@idc-forms/schemas'
import { CircleOff, CirclePlay, FileText, Info, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { PATHS } from '@/app/router/routes'
import { ProjectBadge } from '@/features/projects/components/project-badge'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import {
  useDeleteTypePropertyMutation,
  useUpdateTypePropertyMutation
} from '../hooks/use-type-property-mutations'
import type { TypePropertyAdminRow } from '../models/type-property-admin'
import { TypePropertyActiveBadge } from './type-properties-admin-columns'
import { TypePropertyForm } from './type-property-form'

type TypePropertyDetailPanelProps = {
  typeProperty: TypePropertyAdminRow
}

/**
 * Panel de detalle del subtipo: modo VISTA (resumen + acciones + tab "Más
 * información" con acordeón de actividad) y modo FORMULARIO in-place (mismo
 * TypePropertyForm que la creación). "Plantillas de documentos" navega al
 * editor de plantillas del subtipo (página completa que se conserva).
 */
export function TypePropertyDetailPanel({ typeProperty }: TypePropertyDetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const navigate = useNavigate()
  const updateMutation = useUpdateTypePropertyMutation()
  const deleteMutation = useDeleteTypePropertyMutation()
  const { has } = usePermissions()

  const handleUpdate = (values: CreateTypePropertyInput) => {
    updateMutation.mutate(
      { id: typeProperty._id, payload: values },
      {
        onSuccess: () => {
          setMode('view')
        }
      }
    )
  }

  const toggleActive = () => {
    updateMutation.mutate({ id: typeProperty._id, payload: { active: !typeProperty.active } })
  }

  if (mode === 'edit') {
    return (
      <TypePropertyForm
        defaultValues={{
          project: typeProperty.project,
          code: typeProperty.code,
          name: typeProperty.name ?? undefined,
          active: typeProperty.active
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={handleUpdate}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold leading-tight">{typeProperty.code}</h3>
            {typeProperty.name && (
              <p className="truncate text-sm text-muted-foreground">{typeProperty.name}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <DetailFieldRow
              label="Estado"
              value={<TypePropertyActiveBadge active={typeProperty.active} />}
            />
            <DetailFieldRow
              label="Proyecto"
              value={
                <ProjectBadge name={typeProperty.projectName} code={typeProperty.projectCode} />
              }
            />
            <DetailFieldRow
              label="Código"
              value={typeProperty.code}
              copyValue={typeProperty.code}
            />
            {typeProperty.name && <DetailFieldRow label="Nombre" value={typeProperty.name} />}
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateTypeProperties)
              ? [
                  {
                    label: 'Editar subtipo',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  {
                    label: 'Plantillas de documentos',
                    icon: <FileText className="h-4 w-4" />,
                    variant: 'outline' as const,
                    onClick: () => {
                      closePanel()
                      void navigate(PATHS.admin.business.typeProperties.documents(typeProperty._id))
                    }
                  },
                  {
                    label: typeProperty.active ? 'Desactivar subtipo' : 'Activar subtipo',
                    icon: typeProperty.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteTypeProperties)
              ? [
                  {
                    label: 'Eliminar subtipo',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['activity']}>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow
                    label="Creado"
                    value={formatHumanDateTime(typeProperty.createdAt)}
                  />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={typeProperty.createdBy?.fullname} />}
                  />
                  <DetailFieldRow
                    label="Actualizado"
                    value={formatHumanDateTime(typeProperty.updatedAt)}
                  />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={typeProperty.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar subtipo</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{typeProperty.code}” del catálogo de{' '}
              {typeProperty.projectName ?? 'su proyecto'}. Los inmuebles históricos que lo
              referencian se conservan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(typeProperty._id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
